import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, setDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

/**
 * Tracks daily page view usage per company.
 * Writes to: companies/{companyId}/usageStats/{YYYY-MM-DD}
 * Fields: { pageViews: number, lastActivity: string }
 * Increments on every page navigation.
 */
const useUsageTracker = () => {
  const { activeCompany, currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    // Platform-wide DAU/MAU signal: mark this user as active today.
    // Throttled to ONE write per user per day via localStorage so it never
    // adds meaningful write load. Stored as a uid->timestamp map per day in a
    // top-level doc the Super Admin reads to compute DAU / MAU / stickiness.
    if (currentUser?.uid) {
      try {
        const markKey = `amal_dau_${currentUser.uid}`;
        if (localStorage.getItem(markKey) !== today) {
          setDoc(doc(db, 'platformActivity', today), {
            users: { [currentUser.uid]: new Date().toISOString() },
            updatedAt: new Date().toISOString()
          }, { merge: true })
            .then(() => { try { localStorage.setItem(markKey, today); } catch { /* ignore */ } })
            .catch(() => { /* never break the app */ });
        }
      } catch { /* ignore */ }
    }

    if (!activeCompany?.id) return;

    // Page-view counter, batched (2026-07-16, Firebase cost): accumulate views
    // locally and flush at most once every 5 minutes per company per browser,
    // instead of one Firestore write per navigation fleet-wide. Totals are
    // preserved — the flush writes increment(all pending views).
    try {
      const pendKey = `amal_pv_${activeCompany.id}`;
      const flushKey = `amal_pv_flush_${activeCompany.id}`;
      const pending = (parseInt(localStorage.getItem(pendKey), 10) || 0) + 1;
      const lastFlush = parseInt(localStorage.getItem(flushKey), 10) || 0;

      if (Date.now() - lastFlush < 5 * 60 * 1000) {
        localStorage.setItem(pendKey, String(pending));
        return;
      }

      localStorage.setItem(pendKey, '0');
      localStorage.setItem(flushKey, String(Date.now()));
      const usageRef = doc(db, 'companies', activeCompany.id, 'usageStats', today);
      setDoc(usageRef, {
        pageViews: increment(pending),
        lastActivity: new Date().toISOString()
      }, { merge: true }).catch(() => {
        // Silently fail - usage tracking should never break the app
      });
    } catch {
      // localStorage unavailable (private mode etc.) — skip tracking, never break the app
    }
  }, [activeCompany?.id, currentUser?.uid, location.pathname]);
};

export default useUsageTracker;
